import React, { useMemo } from "react";
import { Trash2, ShieldCheck, Send, Pencil, ArrowRightLeft, FileDown } from "lucide-react";
import { fmt, invoiceRevenueINR, invoiceStatusLabel, INVOICE_STATUSES } from "@/lib/finance";
import DriveLinkCell from "@/components/DriveLinkCell";
import GlobalInvoiceWizard from "@/components/GlobalInvoiceWizard";
import { EditableText, EditableSelect, EditableDate } from "@/components/EditableCell";
import { useFieldSetters } from "@/components/ExpenseLedgerRow";

export const CURRENCIES = ["INR", "USD", "RMB"];
export const STATUS_OPTIONS = INVOICE_STATUSES.map((s) => ({ value: s, label: invoiceStatusLabel(s) }));

// Frozen/sticky columns: Date, Invoice #, Client + Amount. Widths must match
// each sticky <th>/<td>'s inline style.
export const STICKY_W = { checkbox: 36, date: 100, invNum: 150, client: 150, amount: 110 };
export const COL_W = { project: 120, service: 130, hsn: 90, ccy: 70, fx: 80, gst: 70, tds: 70, status: 110, due: 100, received: 110, netreceived: 120, netreceivable: 130, account: 130, firc: 80, invdoc: 90, actions: 220 };

const actionBtn = "px-2 py-1 rounded-sm text-[10px] font-medium inline-flex items-center gap-1 transition-colors";

const RevenueLedgerRow = React.memo(function RevenueLedgerRow({
  row: r, dirty, selected, canBulk, RowCheckbox, updateCell, working, isFounder,
  clientOptions, projectOptions, serviceOptions, sourceOptions,
  onStatusChange, onSubmitForApproval, onApprove, onMoveToExpense, onRemove, onPreview, onSaved,
}) {
  const set = useFieldSetters(r.id, updateCell);
  const receivable = invoiceRevenueINR(r);
  const status = (r.status || "draft").toLowerCase();
  const isDraftStatus = status === "draft";
  const isPendingApproval = status === "pending_approval";
  const stickyBg = r.needs_firc ? "bg-yellow-50" : dirty ? "bg-orange-50" : selected ? "bg-orange-100" : "bg-white";
  const stickyLeft = canBulk ? STICKY_W.checkbox : 0;

  const setClient = (v) => { updateCell(r.id, "client_id", v); const c = clientOptions.find((x) => x.value === v); if (c) updateCell(r.id, "client_name", c.label); };
  const setProject = (v) => { updateCell(r.id, "project_id", v); const p = projectOptions.find((x) => x.value === v); if (p) updateCell(r.id, "project_name", p.label); };
  const editTrigger = useMemo(() => (
    <button title="Edit invoice" className={`${actionBtn} text-slate-700 hover:text-white hover:bg-slate-700 border border-slate-200 hover:border-slate-700`} data-testid={`rev-ledger-edit-${r.id}`}>
      <Pencil size={11} />
    </button>
  ), [r.id]);

  return (
    <tr className={`hover:bg-slate-50 ${r.needs_firc ? "bg-yellow-50" : ""} ${dirty ? "!bg-orange-50 border-l-2 border-[#F54800]" : ""} ${selected ? "!bg-orange-100" : ""}`} data-testid={`rev-ledger-row-${r.id}`}>
      {canBulk && (
        <td className={`w-9 px-2 py-2 sticky left-0 z-10 ${stickyBg}`} style={{ width: STICKY_W.checkbox }}><RowCheckbox id={r.id} /></td>
      )}
      <td className={`px-2 py-2 sticky z-10 ${stickyBg}`} style={{ left: stickyLeft, width: STICKY_W.date }}><EditableDate value={r.accrual_date || r.date || ""} onChange={set("accrual_date")} placeholder="Undated" testId={`rev-cell-date-${r.id}`} /></td>
      <td className={`px-2 py-2 sticky z-10 ${stickyBg}`} style={{ left: stickyLeft + STICKY_W.date, width: STICKY_W.invNum }}><EditableText className="min-w-[150px] font-mono" value={r.invoice_number} onChange={set("invoice_number")} commitOnBlur testId={`rev-cell-number-${r.id}`} /></td>
      <td className={`px-2 py-2 sticky z-10 ${stickyBg}`} style={{ left: stickyLeft + STICKY_W.date + STICKY_W.invNum, width: STICKY_W.client }}>
        <EditableSelect value={r.client_id} onChange={setClient} options={clientOptions} placeholder={r.client_name || "Pick client"} testId={`rev-cell-client-${r.id}`} />
      </td>
      <td className={`px-2 py-2 sticky z-10 ${stickyBg} shadow-[4px_0_6px_-4px_rgba(0,0,0,0.15)]`} style={{ left: stickyLeft + STICKY_W.date + STICKY_W.invNum + STICKY_W.client, width: STICKY_W.amount }}><EditableText type="number" align="right" className="w-20" value={r.amount} onChange={set("amount")} commitOnBlur testId={`rev-cell-amount-${r.id}`} /></td>
      <td className="px-2 py-2 w-32"><EditableSelect value={r.project_id} onChange={setProject} options={projectOptions} placeholder={r.project_name || "—"} testId={`rev-cell-project-${r.id}`} /></td>
      <td className="px-2 py-2 w-32"><EditableSelect value={r.service_type} onChange={set("service_type")} options={serviceOptions} placeholder="Unassigned" testId={`rev-cell-service-${r.id}`} /></td>
      <td className="px-2 py-2"><EditableText className="w-20 font-mono" value={r.hsn_code} onChange={set("hsn_code")} commitOnBlur testId={`rev-cell-hsn-${r.id}`} /></td>
      <td className="px-2 py-2 w-16"><EditableSelect value={r.currency || "INR"} onChange={set("currency")} options={CURRENCIES} /></td>
      <td className="px-2 py-2"><EditableText type="number" align="right" className="w-16" value={r.fx_rate} onChange={set("fx_rate")} commitOnBlur testId={`rev-cell-fx-${r.id}`} /></td>
      <td className="px-2 py-2"><EditableText type="number" align="right" className="w-14" value={r.gst} onChange={set("gst")} commitOnBlur /></td>
      <td className="px-2 py-2"><EditableText type="number" align="right" className="w-14" value={r.tds} onChange={set("tds")} commitOnBlur /></td>
      <td className="px-2 py-2 w-24"><EditableSelect value={r.status} onChange={(v) => onStatusChange(r, v)} options={STATUS_OPTIONS} testId={`rev-cell-status-${r.id}`} /></td>
      <td className="px-2 py-2"><EditableDate value={r.due_date} onChange={set("due_date")} testId={`rev-cell-due-${r.id}`} /></td>
      <td className="px-2 py-2"><EditableDate value={r.payment_received_date} onChange={set("payment_received_date")} placeholder="Not received" testId={`rev-cell-received-date-${r.id}`} /></td>
      <td className="px-2 py-2"><EditableText type="number" align="right" className="w-24" value={r.net_received_inr} onChange={set("net_received_inr")} commitOnBlur placeholder="auto" testId={`rev-cell-net-received-${r.id}`} /></td>
      <td className="px-2 py-2 text-xs font-mono text-right whitespace-nowrap">{receivable != null ? fmt(receivable) : "—"}</td>
      <td className="px-2 py-2 w-32"><EditableSelect value={r.source_id} onChange={set("source_id")} options={sourceOptions} placeholder="Unassigned" testId={`rev-cell-source-${r.id}`} /></td>
      <td className="px-2 py-2 text-xs">
        <DriveLinkCell value={r.firc_url} onChange={set("firc_url")} onPreview={(url) => onPreview({ url, title: `FIRC — ${r.invoice_number || r.id}` })} testId={`rev-cell-firc-${r.id}`} />
      </td>
      <td className="px-2 py-2">
        <DriveLinkCell value={r.drive_link} onChange={set("drive_link")} onPreview={(url) => onPreview({ url, title: `Invoice document — ${r.invoice_number || r.id}` })} testId={`rev-cell-drive-link-${r.id}`} />
      </td>
      <td className="px-2 py-2 text-right">
        <div className="inline-flex gap-1.5 items-center">
          {isDraftStatus && (
            <button
              onClick={() => onSubmitForApproval(r)}
              disabled={working || !r.drive_link}
              title={r.drive_link ? "Submit for Founder approval" : "Attach a PDF / Drive link before submitting"}
              className={`${actionBtn} text-white bg-slate-700 hover:bg-slate-800 border border-slate-700 disabled:opacity-40 disabled:cursor-not-allowed`}
              data-testid={`rev-ledger-submit-approval-${r.id}`}
            >
              <Send size={11} /> {working ? "…" : "Submit"}
            </button>
          )}
          {isPendingApproval && isFounder && (
            <button
              onClick={() => onApprove(r)}
              disabled={working || !r.hsn_code || !r.drive_link}
              title={r.hsn_code && r.drive_link ? "Approve — HSN + PDF verified" : "Add HSN code + drive link (PDF) before approving"}
              className={`${actionBtn} text-white bg-green-600 hover:bg-green-700 border border-green-600 disabled:opacity-40 disabled:cursor-not-allowed`}
              data-testid={`rev-ledger-approve-${r.id}`}
            >
              <ShieldCheck size={11} /> {working ? "…" : "Approve"}
            </button>
          )}
          {!isDraftStatus && !isPendingApproval && (
            <button
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/finance/invoices/${r.id}/pdf`, "_blank")}
              title="Generate PDF — studio branding, Service Description, HSN and GST total"
              className={`${actionBtn} text-slate-700 hover:text-white hover:bg-[#F54800] border border-slate-200 hover:border-[#F54800]`}
              data-testid={`rev-ledger-generate-pdf-${r.id}`}
            >
              <FileDown size={11} /> PDF
            </button>
          )}
          <GlobalInvoiceWizard invoice={r} onSaved={onSaved} trigger={editTrigger} />
          <button
            onClick={() => onMoveToExpense(r)}
            title="Move to Expenses — correct an accidental Revenue entry"
            className={`${actionBtn} text-slate-700 hover:text-white hover:bg-[#F54800] border border-slate-200 hover:border-[#F54800]`}
            data-testid={`rev-ledger-move-to-expense-${r.id}`}
          >
            <ArrowRightLeft size={11} /> Move to Expenses
          </button>
          <button onClick={() => onRemove(r.id)} className={`${actionBtn} text-slate-700 hover:text-white hover:bg-red-600 border border-slate-200 hover:border-red-600`} data-testid={`rev-ledger-del-${r.id}`}>
            <Trash2 size={11} />
          </button>
        </div>
      </td>
    </tr>
  );
});

export default RevenueLedgerRow;
